import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, message, Modal, Row, Spin, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ACCOUNT_TYPE, CHINA_NATIONALITY_VALUE, HONG_KONG_NATIONALITY_VALUE } from 'src/constants';
import { PAGE_ROUTE } from 'src/constants/route';
import { LETTER_REGEX } from 'src/constants/validation';
import { normalizeInputBlockNumber } from 'src/helpers/common.utils';
import { noop } from 'src/helpers/window';
import useStore from 'src/hooks/useStore';
import { IAccountOnboardingDetail } from 'src/interfaces/accounts/account';
import { ICompanyInfo } from 'src/interfaces/accounts/company';
import { i18nKey } from 'src/locales/i18n';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';
import { IUserStore } from 'src/store/user';
import styles from './e-form-mortgage-deal.module.less';

interface IProps {
	open: boolean;
	onClose?: () => void;
}

const EFormMortgageDeal = ({ open, onClose }: IProps) => {
	const [t] = useTranslation();

	const navigator = useNavigate();

	const userStore: IUserStore = useStore('userStore');

	const accountDetails: IAccountOnboardingDetail = userStore.accountDetail || {};

	const companyDetails: ICompanyInfo = userStore.companyDetail || {};

	const mortgageDetailsStore: IMortgageDealDetails = useStore('mortgageDealDetails');

	const thirdPartyReferralList = mortgageDetailsStore.mortgageDealForm.thirdPartyReferral || [];

	const [loading, setLoading] = useState(false);

	const [form] = Form.useForm();

	const onAccept = async () => {
		try {
			await form.validateFields();

			let stopDueToError = false;
			if (accountDetails.firstName?.toLowerCase() !== form.getFieldValue('firstName').toLowerCase()) {
				form.setFields([
					{
						name: 'firstName',
						errors: [t(i18nKey.eform.firstNameNotMatched)]
					}
				]);
				stopDueToError = true;
			}
			const mName = form.getFieldValue('middleName');
			if (mName && accountDetails.middleName?.toLowerCase() !== mName.toLowerCase()) {
				form.setFields([
					{
						name: 'middleName',
						errors: [t(i18nKey.eform.middleNameNotMatched)]
					}
				]);
				stopDueToError = true;
			}

			if (accountDetails.lastName?.toLowerCase() !== form.getFieldValue('lastName').toLowerCase()) {
				form.setFields([
					{
						name: 'lastName',
						errors: [t(i18nKey.eform.lastNameNotMatched)]
					}
				]);
				stopDueToError = true;
			}

			if (stopDueToError) {
				return;
			}

			const rs: boolean = await mortgageDetailsStore.submitMortgageDealForm();
			if (rs) {
				message.success(t(i18nKey.mortgageDeal.requestForm.message.submitSuccess));
				navigator(PAGE_ROUTE.DASHBOARD.concat(PAGE_ROUTE.DASHBOARD_MORTGAGE));
				mortgageDetailsStore.dispose();
			} else {
				form.setFieldValue('firstName', '');
				form.setFieldValue('middleName', '');
				form.setFieldValue('lastName', '');
			}
		} catch {
			noop();
		}
	};

	const renderName = (): string => {
		if (userStore.accountTypeMap === ACCOUNT_TYPE.INDIVIDUAL) {
			return `${accountDetails.firstName} ${accountDetails.middleName ? accountDetails.middleName : ''} ${
				accountDetails.lastName
			}`;
		}

		return `${companyDetails.name}`;
	};

	const renderTypeIdentity = () => {
		const nationality = userStore.accountDetail?.nationality;

		if (userStore.accountTypeMap === ACCOUNT_TYPE.INDIVIDUAL) {
			switch (nationality) {
				case HONG_KONG_NATIONALITY_VALUE:
					return {
						typeName: `${t(i18nKey.mortgageDeal.eForm.hkIdentity)}`,
						id: `${accountDetails.hkIdPart1}${accountDetails.hkId}${accountDetails.hkIdPart2}`
					};
				case CHINA_NATIONALITY_VALUE:
					return { typeName: `${t(i18nKey.mortgageDeal.eForm.cnIdentity)}`, id: `${accountDetails.chineseIdNo}` };

				default:
					return { typeName: `${t(i18nKey.mortgageDeal.eForm.passportNo)}`, id: `${accountDetails.passportNo}` };
			}
		}

		return { typeName: `${t(i18nKey.mortgageDeal.eForm.corporateNo)}`, id: `${companyDetails.brRegistrationNo}` };
	};

	const renderAddress = () => {
		if (userStore.accountTypeMap === ACCOUNT_TYPE.INDIVIDUAL) {
			return `${accountDetails.addressLine}, ${accountDetails.street}, ${accountDetails.district}, ${
				accountDetails.country ? accountDetails.country : ''
			}`;
		}

		return `${companyDetails.registeredAddress}`;
	};

	useEffect(() => {
		setLoading(true);
		userStore.fetchUserInfo().then().catch();
		setLoading(false);
	}, []);

	return (
		<>
			<Modal
				open={open}
				destroyOnClose
				width={987}
				style={{ top: 20 }}
				bodyStyle={{ overflowY: 'scroll', maxHeight: '700px' }}
				onCancel={onClose}
				title={null}
				footer={null}
				closeIcon={
					<CloseOutlined
						style={{
							fontSize: '2rem'
						}}
					/>
				}>
				<Spin spinning={loading}>
					<div
						style={{
							marginTop: '2rem'
						}}>
						<Typography.Title level={2}>{t(i18nKey.mortgageDeal.eForm.title)}</Typography.Title>
					</div>
					<div style={{ padding: 10 }}>
						<Row>
							<Col>
								{t(i18nKey.mortgageDeal.eForm.confirmationFrom)}{' '}
								{userStore.accountTypeMap === ACCOUNT_TYPE.INDIVIDUAL ? (
									<>
										<span className={styles.commonFont}>{`[${accountDetails.firstName} ${
											accountDetails.middleName ? accountDetails.middleName : ''
										} ${accountDetails.lastName}]`}</span>
									</>
								) : (
									<>
										<span className={styles.commonFont}>{`[${companyDetails.name}]`}</span>
									</>
								)}
							</Col>
						</Row>
						<Row>
							<Col>{t(i18nKey.mortgageDeal.eForm.to)}</Col>
						</Row>
						<Row style={{ marginBottom: 30 }}>
							<Col>
								{t(i18nKey.mortgageDeal.eForm.no)}
								<span className={styles.commonFont}>{renderName()}</span>
								{', '}
								{t(i18nKey.mortgageDeal.eForm.holderOf)} {'[ '}
								<span>{renderTypeIdentity().typeName}</span>
								<span className={styles.commonFont}>{`${renderTypeIdentity().id}`}</span>
								{'] '}
								{userStore.accountTypeMap === ACCOUNT_TYPE.CORPORATE && (
									<>
										{t(i18nKey.mortgageDeal.eForm.companyNumber)}
										<span className={styles.commonFont}>{companyDetails.ciNo}</span>
										{'] *'}
									</>
								)}
								{t(i18nKey.mortgageDeal.eForm.address)}
								<span className={styles.commonFont}>{renderAddress()}</span>
								{t(i18nKey.mortgageDeal.eForm.refer)}
							</Col>
						</Row>

						{thirdPartyReferralList.length > 0 ? (
							<>
								<Row>
									<Col span={24}>{t(i18nKey.mortgageDeal.eForm.radioOption1)}</Col>
								</Row>
								<Form
									initialValues={{
										mainReferral: mortgageDetailsStore.mainReferral,
										thirdPartyReferral: {
											referralList: mortgageDetailsStore.referralList
										}
									}}>
									<Row style={{ marginTop: 24 }}>
										<Col>{t(i18nKey.mortgageDeal.eForm.second)}</Col>
									</Row>
									<Row style={{ marginBottom: 30 }}>
										<div className={styles.box}>
											<Row>
												<Col xs={24} sm={24} md={24}>
													<Typography.Title level={3}>
														{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.referralList)}
													</Typography.Title>
												</Col>
											</Row>
											<Row gutter={[10, 20]} style={{ marginBottom: 10 }}>
												<Col xs={24} sm={24} md={24} lg={12}>
													<Row>
														<Typography.Title level={5}>
															{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyNameOrIndividualName)}*
														</Typography.Title>
													</Row>
													<Row className={styles.formBox}>
														<Form.Item
															name={['mainReferral', 'companyNameOrIndividualName']}
															required={false}
															style={{ width: '100%' }}>
															<Input disabled />
														</Form.Item>
													</Row>
												</Col>
												<Col xs={24} sm={24} md={24} lg={12}>
													<Row>
														<Typography.Title level={5}>
															{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.address)}*
														</Typography.Title>
													</Row>
													<Row className={styles.formBox}>
														<Form.Item name={['mainReferral', 'address']} style={{ width: '100%' }}>
															<Input disabled />
														</Form.Item>
													</Row>
												</Col>
											</Row>
											<Form.List name={['thirdPartyReferral', 'referralList']}>
												{(fields) => (
													<>
														{fields.map((f, idx) => (
															<Row gutter={[10, 20]} key={f.key} style={{ marginBottom: 10 }}>
																<Col xs={24} sm={24} md={24} lg={12}>
																	<Row>
																		<Typography.Title level={5}>
																			{t(
																				i18nKey.mortgageDeal.requestForm.mortgageDealDetails.companyNameOrIndividualName
																			)}
																			*
																		</Typography.Title>
																	</Row>
																	<Row className={styles.formBox}>
																		<Form.Item name={[idx, 'companyNameOrIndividualName']} style={{ width: '100%' }}>
																			<Input disabled />
																		</Form.Item>
																	</Row>
																</Col>
																<Col xs={24} sm={24} md={24} lg={12}>
																	<Row>
																		<Typography.Title level={5}>
																			{t(i18nKey.mortgageDeal.requestForm.mortgageDealDetails.address)}*
																		</Typography.Title>
																	</Row>
																	<Row className={styles.formBox}>
																		<Form.Item name={[idx, 'address']} style={{ width: '100%' }}>
																			<Input disabled />
																		</Form.Item>
																	</Row>
																</Col>
															</Row>
														))}
													</>
												)}
											</Form.List>
										</div>
									</Row>
									<Row>
										<Col>{t(i18nKey.mortgageDeal.eForm.descriptionForSecondRole)}</Col>
									</Row>
								</Form>
							</>
						) : (
							<Row style={{ marginBottom: 20 }}>
								<Col span={24}>{t(i18nKey.mortgageDeal.eForm.radioOption2)}</Col>
							</Row>
						)}
						<Row>
							<Col>{t(i18nKey.mortgageDeal.eForm.important)}</Col>
						</Row>
						<Row>
							<Col>{t(i18nKey.mortgageDeal.eForm.importantNote)}</Col>
						</Row>
						<Row>
							<Col>{t(i18nKey.mortgageDeal.eForm.importantNote2)}</Col>
						</Row>
					</div>
					<Form
						form={form}
						layout="vertical"
						// disabled={disabled}
					>
						<Row justify={'center'} align="middle" gutter={[40, 16]} style={{ padding: 30 }}>
							<Col xs={24} sm={24} md={8}>
								<Form.Item
									required={false}
									name="firstName"
									label={`${t(i18nKey.registration.firstName)} *`}
									normalize={normalizeInputBlockNumber}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										},
										{
											pattern: LETTER_REGEX,
											message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
							<>
								<Col xs={24} sm={24} md={8}>
									<Form.Item
										name="middleName"
										label={
											accountDetails.middleName
												? `${t(i18nKey.registration.middleName)} *`
												: `${t(i18nKey.registration.middleName)}`
										}
										required={false}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: accountDetails.middleName ? true : false,
												message: accountDetails.middleName
													? `${t(i18nKey.onboarding.validation.required)}`
													: ''
											},
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</>

							<Col xs={24} sm={24} md={8}>
								<Form.Item
									required={false}
									name="lastName"
									normalize={normalizeInputBlockNumber}
									label={`${t(i18nKey.registration.lastName)} *`}
									rules={[
										{
											required: true,
											message: `${t(i18nKey.onboarding.validation.required)}`
										},
										{
											pattern: LETTER_REGEX,
											message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
										}
									]}>
									<Input />
								</Form.Item>
							</Col>
						</Row>
					</Form>
					<Row justify={'center'} align="middle">
						<Col span={20}>
							<Button
								block
								type="primary"
								style={{ whiteSpace: 'normal', height: 'auto', marginBottom: '10px' }}
								onClick={onAccept}>
								{t(i18nKey.mortgageDeal.eForm.submitButton)}
							</Button>
						</Col>
					</Row>
				</Spin>
			</Modal>
		</>
	);
};

export default observer(EFormMortgageDeal);
